import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLocalUser } from '../supabase';
import { theme } from '../theme';
import { LogoCompact } from '../Icons';
import AccountScreenBanner from './AccountScreenBanner';
import { getNotificationPreferences, updateNotificationPreferences } from '../api/atlast';

// NotificationPreferencesScreen — new (Sep 7, per Ali's report — Phase
// 2 of the account-settings menu). Payment-failure alerts are
// deliberately not listed here at all, matching the backend's own
// get_notification_preferences response — that one stays mandatory,
// per Ali's explicit call, not a toggle that could be silently left
// off before a real-money problem. (Also worth being precise about
// what "mandatory" means here, Sep 8: send_critical_alert goes
// entirely to Ali's own admin inbox, not the user — there is no
// user-facing payment-failure email at all right now. The original
// subtitle line on this screen implied the user themselves always
// gets notified, which wasn't true; removed rather than reworded,
// since there's nothing accurate and still relevant to say to a user
// about an alert that was never theirs to receive.)
//
// "Streaks & referrals" — removed Sep 8 (real audit finding at the
// time: no streak or referral email existed anywhere yet, so a toggle
// controlling nothing real was worse than no toggle at all). Restored
// (Sep 15, per Ali's report) now that send_streak_milestone_email and
// send_referral_joined_email actually exist and both genuinely check
// streak_referral_emails_opted_out before sending — the column and
// backend endpoint had been left in place the whole time specifically
// for this moment.
const TOGGLES = [
  {
    key: 'deal_alert_emails_opted_out',
    label: 'Deal found',
    description: 'An email when one of your trips has a fare under your budget.'
  },
  {
    key: 'freeze_resolution_emails_opted_out',
    label: 'Price Watch results',
    description: 'When a Price Watch ends, with whether you pay or get a refund and how long you have to send proof. This also covers the reminder before a watch ends and one feedback request afterwards.'
  },
  {
    key: 'streak_referral_emails_opted_out',
    label: 'Streaks and referrals',
    description: 'When you reach a 7-day streak, or when someone joins with your referral link. Both give you an extra trip.'
  }
];

// On is green, off is grey, and the word is shown beside the switch, so
// the state never depends on telling two similar colours apart.
function Toggle({ on, onClick, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '8px', width: '92px', flexShrink: 0,
        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        fontSize: '13px', fontWeight: 700, color: theme.textPrimary, fontFamily: 'inherit'
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: '42px', height: '24px', borderRadius: '12px', position: 'relative', flexShrink: 0,
          background: on ? '#17663A' : '#9AA8B8', transition: 'background 0.15s', display: 'inline-block'
        }}
      >
        <span style={{
          width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
          position: 'absolute', top: '3px', left: on ? '21px' : '3px', transition: 'left 0.15s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.25)'
        }} />
      </span>
      {on ? 'On' : 'Off'}
    </button>
  );
}

function NotificationPreferencesScreen() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getLocalUser().then(async ({ data }) => {
      if (!data?.user?.id) return;
      setUserId(data.user.id);
      try {
        const result = await getNotificationPreferences(data.user.id);
        setPrefs(result.preferences);
      } catch (e) {
        setError('Could not load your preferences. Try again.');
      }
      setLoading(false);
    });
  }, []);

  const handleToggle = async (key) => {
    const newValue = !prefs[key];
    // Optimistic — flips immediately, rolls back only if the save
    // actually fails, so a normal toggle feels instant rather than
    // waiting on a round-trip for something this low-stakes.
    setPrefs(prev => ({ ...prev, [key]: newValue }));
    try {
      await updateNotificationPreferences(userId, { [key]: newValue });
    } catch (e) {
      setPrefs(prev => ({ ...prev, [key]: !newValue }));
      setError('Could not save that change. Try again.');
    }
  };

  return (
    <>
      <AccountScreenBanner section="Notifications" />
      <div className="login-form-panel" style={{ minHeight: 'calc(100vh - 60px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="login-card yedge" style={{ maxWidth: '440px' }}>
          <div className="login-card-body">
          <div className="login-card-icon">
            <LogoCompact width={150} color="#1E3A5F" />
          </div>
          <h2 style={{ textAlign: 'center', margin: '0 0 4px', color: theme.textPrimary, fontFamily: theme.fontDisplay }}>
            Notifications
          </h2>
          <p style={{ textAlign: 'center', color: theme.textMuted, margin: '0 0 24px' }}>
            Choose which emails you get from us.
          </p>

          {loading ? (
            <p style={{ textAlign: 'center', color: theme.textMuted, fontSize: '13px' }}>Loading…</p>
          ) : !prefs ? (
            <p style={{ textAlign: 'center', color: theme.danger, fontSize: '13px' }}>{error}</p>
          ) : (
            <>
              {TOGGLES.map((t, i) => (
                <div
                  key={t.key}
                  style={{
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '14px',
                    padding: '14px 0', borderBottom: i < TOGGLES.length - 1 ? `1px solid ${theme.border}` : 'none'
                  }}
                >
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: theme.textPrimary }}>{t.label}</div>
                    <div style={{ fontSize: '13px', color: theme.textMuted, marginTop: '2px', lineHeight: 1.45 }}>{t.description}</div>
                  </div>
                  <Toggle on={!prefs[t.key]} label={t.label} onClick={() => handleToggle(t.key)} />
                </div>
              ))}

              {error && (
                <p style={{ fontSize: '12.5px', color: theme.danger, marginTop: '14px', marginBottom: 0, fontWeight: 600 }}>{error}</p>
              )}

              <button
                className="btn-primary login-submit-btn"
                style={{ marginTop: '22px', width: '100%' }}
                onClick={() => navigate('/dashboard')}
              >
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

export default NotificationPreferencesScreen;